/**
 * 事务数据上下文 - 管理核心事务数据
 *
 * 职责：
 * - 核心只管理从服务器获取的 transactions 数据
 * - 相关的加载/错误状态 (dataLoadingStatus, errors)
 * - 工作流模式 (workflowMode)
 * - 使其成为一个纯粹的数据仓库
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useState, useRef } from "react";
import { message } from "antd";
import { batchTransactionReducer, initialState, actionCreators } from "../store/reducer";
import { DATA_LOADING_STATUS, CLIENT_STATUS, TRANSACTION_STATUS, SUPPORTED_TRANSACTION_TYPES } from "../types";
import { useTransactionManager } from "../hooks/useTransactionManager";
import { useBatchTransactionOrchestrator } from "../hooks/useBatchTransactionOrchestrator";
import { useBatchTransactionAPI } from "../hooks/useBatchTransactionAPI";
import { isFlightTimeMatchProduct } from "../../../../utils/timeRangeUtils";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";

// 创建Context
const TransactionDataContext = createContext();

/**
 * 事务数据Provider组件
 */
export const TransactionDataProvider = ({ children, initialFlights, transactionType, selectionManager }) => {
    const [state, dispatch] = useReducer(batchTransactionReducer, initialState);

    // 初始化基础状态 - 智能防重复初始化逻辑
    const [lastInitializedKey, setLastInitializedKey] = useState(null);

    // 跟踪事务类型变更
    const prevTransactionTypeRef = useRef(transactionType);

    // API Hook
    const { initializeBatchTransactionData } = useBatchTransactionAPI();

    // 获取当前时区设置和机场映射
    const { timeZone, airportsMap } = useTimeZone();

    // 使用事务管理器Hook
    const transactionManager = useTransactionManager(state.transactions, state.selectedFlights, transactionType, state.workflowMode, selectionManager.selectedSupplier, selectionManager.activeAirports, dispatch, actionCreators);

    // 使用编排器Hook
    const orchestrator = useBatchTransactionOrchestrator(initialFlights, transactionType, selectionManager, state, dispatch, actionCreators);

    // 自动添加单一产品的逻辑
    const autoAddRan = useRef(false);

    // 当选择的供应商或初始化key变化时，重置自动添加标志
    useEffect(() => {
        autoAddRan.current = false;
    }, [selectionManager.selectedSupplier, lastInitializedKey]);

    // 自动为新事务添加唯一可用产品
    useEffect(() => {
        const { availableProducts, selectedSupplier } = selectionManager;
        const { transactions, workflowMode, dataLoadingStatus } = state;

        // 确保数据加载完成，且处于创建模式
        if (dataLoadingStatus !== DATA_LOADING_STATUS.SUCCESS || workflowMode !== "CREATE") {
            return;
        }

        const shouldRun = !autoAddRan.current && selectedSupplier && availableProducts?.length > 0 && transactions?.length > 0;

        if (!shouldRun) {
            return;
        }

        const supplierProducts = availableProducts.filter((p) => p.partner_id === selectedSupplier.id);
        const selectedFlights = state.selectedFlights || [];

        console.log("🤖 开始为每个航班单独匹配产品:", {
            supplier: selectedSupplier.name,
            totalSupplierProducts: supplierProducts.length,
            flights: selectedFlights.length,
            flightTimes: selectedFlights.map((f) => ({ id: f.id, std: f.std, etd: f.etd })),
        });

        // 新逻辑：为每个航班单独判断是否需要自动添加产品
        const transactionsToUpdate = transactions.filter((t) => t.isNewTransaction && t.details.length === 0);
        let autoAddCount = 0;
        const autoAddResults = [];

        transactionsToUpdate.forEach((transaction) => {
            // 找到对应的航班
            const flight = selectedFlights.find((f) => f.id === transaction.flightId);
            if (!flight) {
                console.log(`🤖 未找到事务 ${transaction.id} 对应的航班`);
                return;
            }

            // 为该航班筛选匹配的产品
            const matchingProducts = supplierProducts.filter((product) => {
                // 如果产品没有时间限制，则始终匹配
                if (!product.applicable_time_ranges || product.applicable_time_ranges.length === 0) {
                    return true;
                }
                // 检查航班时间是否匹配产品时间区间
                // 注意：现在使用机场本地时间进行正确的时区转换匹配
                return isFlightTimeMatchProduct(flight, product, timeZone, airportsMap);
            });

            console.log(
                `🤖 航班 ${flight.id} (${flight.std || flight.etd}) 匹配到 ${matchingProducts.length} 个产品:`,
                matchingProducts.map((p) => ({ name: p.product_name, timeRanges: p.applicable_time_ranges })),
            );

            // 如果该航班只匹配到一个产品，自动添加
            if (matchingProducts.length === 1) {
                const productToAdd = matchingProducts[0];
                transactionManager.addDetailWithPermitType(transaction.id, productToAdd);
                autoAddCount++;
                autoAddResults.push({
                    flightId: flight.id,
                    flightTime: flight.std || flight.etd,
                    productName: productToAdd.product_name,
                });

                console.log(`🤖 为航班 ${flight.id} 自动添加产品: ${productToAdd.product_name}`);
            }
        });

        // 显示自动添加结果
        if (autoAddCount > 0) {
            const resultMessage = autoAddResults.map((r) => `航班${r.flightId}(${r.flightTime?.substring(11, 16) || "N/A"}): ${r.productName}`).join(", ");

            message.info(`已为 ${autoAddCount} 个航班自动添加产品: ${resultMessage}`);
            autoAddRan.current = true; // 标记为已运行，防止重复执行
        } else {
            console.log("🤖 没有航班可以自动添加产品:", {
                supplier: selectedSupplier.name,
                totalTransactions: transactionsToUpdate.length,
                reason: "每个航班都匹配到多个产品或没有匹配的产品",
            });
        }
    }, [selectionManager.availableProducts, selectionManager.selectedSupplier, state.transactions, state.workflowMode, state.dataLoadingStatus, transactionManager]);

    // 基础操作
    const setLoading = useCallback((loading) => {
        dispatch(actionCreators.setLoading(loading));
    }, []);

    const setDataLoadingStatus = useCallback((status) => {
        dispatch(actionCreators.setDataLoadingStatus(status));
    }, []);

    const setWorkflowMode = useCallback((mode) => {
        dispatch(actionCreators.setWorkflowMode(mode));
    }, []);

    const setTransactions = useCallback((transactions) => {
        dispatch(actionCreators.setTransactions(transactions));
    }, []);

    // 错误处理
    const setError = useCallback((key, error) => {
        dispatch(actionCreators.setError(key, error));
    }, []);

    const clearError = useCallback((key) => {
        dispatch(actionCreators.clearError(key));
    }, []);

    // UI状态
    const setUIState = useCallback((key, value) => {
        dispatch(actionCreators.setUIState(key, value));
    }, []);

    // 重置状态
    const resetState = useCallback(() => {
        dispatch(actionCreators.resetState());
    }, []);

    // 初始化基础状态
    useEffect(() => {
        if (initialFlights && transactionType) {
            // 创建一个唯一键来标识当前的初始化参数
            const currentKey = `${transactionType}-${initialFlights
                .map((f) => f.id)
                .sort()
                .join(",")}`;

            // 只有当参数真正发生变化时才重新初始化
            if (currentKey !== lastInitializedKey) {
                // 验证事务类型是否支持
                if (!SUPPORTED_TRANSACTION_TYPES[transactionType]) {
                    const supportedTypes = Object.keys(SUPPORTED_TRANSACTION_TYPES).join(", ");
                    const errorMessage = `不支持的事务类型: ${transactionType}。当前支持的类型: ${supportedTypes}`;
                    console.error("❌ TransactionDataProvider:", errorMessage);
                    setError("initialization", errorMessage);
                    return;
                }

                console.log("🔄 初始化TransactionData状态:", {
                    initialFlights: initialFlights.length,
                    transactionType,
                    flightIds: initialFlights.map((f) => f.id),
                    previousKey: lastInitializedKey,
                    currentKey,
                });

                dispatch(actionCreators.initializeState(initialFlights, transactionType));
                setLastInitializedKey(currentKey);
            }
        }
    }, [initialFlights, transactionType, lastInitializedKey, setError]);

    // 事务数据加载逻辑 - 只有在选择了供应商后才加载事务数据
    useEffect(() => {
        const loadTransactionData = async () => {
            // 检查是否需要加载事务数据：必须有选中的供应商和至少一个机场，且已完成基础初始化
            if (!state.selectedFlights.length || !state.transactionType || !selectionManager.selectedSupplier || !selectionManager.activeAirports.length || state.dataLoadingStatus !== DATA_LOADING_STATUS.IDLE || !lastInitializedKey) {
                return;
            }

            console.log("🔄 开始加载事务数据:", {
                flights: state.selectedFlights.length,
                supplier: selectionManager.selectedSupplier?.name,
                airports: selectionManager.activeAirports,
            });

            // 显示事务列表加载状态
            orchestrator.showTransactionListLoading();
            dispatch(actionCreators.setDataLoadingStatus(DATA_LOADING_STATUS.LOADING));

            try {
                const flightIds = state.selectedFlights.map((f) => f.id);
                const result = await initializeBatchTransactionData(flightIds, state.transactionType, selectionManager.selectedSupplier.id, selectionManager.activeAirports);

                if (result.success) {
                    // 设置工作流模式
                    dispatch(actionCreators.setWorkflowMode(result.workflowMode));

                    // 获取筛选后的航班列表
                    const filteredFlightIds = result.filteredFlightIds || result.flightIds;
                    const filteredFlights = state.selectedFlights.filter((flight) => filteredFlightIds.includes(flight.id));

                    // 提示用户如果有航班被过滤
                    const totalCount = state.selectedFlights.length;
                    const validCount = filteredFlights.length;
                    const ignoredCount = totalCount - validCount;

                    if (ignoredCount > 0) {
                        const isOriginOnlyService = ["CATERING_ORDER", "FUEL_REQUEST"].includes(state.transactionType);
                        let reason = "不匹配当前所选机场";
                        if (isOriginOnlyService) {
                            reason = "非始发机场(此服务仅限始发)";
                        }

                        if (validCount === 0) {
                            // 如果所有航班都被过滤，使用 warning
                            message.warning(`未找到匹配航班。所有 ${ignoredCount} 个航班均因${reason}被忽略。請檢查所选机场是否正确。`, 5);
                        } else {
                            // 部分过滤，使用 info
                            message.info(`已加载 ${validCount} 个航班，自动忽略 ${ignoredCount} 个${reason}的航班。`);
                        }
                    }

                    // 统一处理事务：只为筛选后的航班创建事务对象
                    const allTransactions = filteredFlights.map((flight) => {
                        const existingTransaction = result.transactions?.find((t) => t.flightId === flight.id);

                        if (existingTransaction) {
                            // 优先使用航班快照信息，如果没有快照则使用实时航班信息
                            const flightDisplayInfo = existingTransaction.flightDisplayInfo || {};
                            const useSnapshot = existingTransaction.hasFlightSnapshot && flightDisplayInfo.data_source === "snapshot";

                            // 转换已存在的事务数据
                            return {
                                id: existingTransaction.id,
                                flightId: existingTransaction.flightId,
                                supplierId: existingTransaction.supplierId,

                                // 航班显示信息 - 优先使用快照数据
                                flightNumber: flightDisplayInfo.flight_number || flight.flight_number || "",
                                flightDate: flightDisplayInfo.flight_date || flight.flight_date || "",
                                route: flightDisplayInfo.route || `${flight.departure_airport || ""}-${flight.arrival_airport || ""}`,
                                departureAirport: flightDisplayInfo.departure_airport || flight.departure_airport || "",
                                arrivalAirport: flightDisplayInfo.arrival_airport || flight.arrival_airport || "",
                                aircraftReg: flightDisplayInfo.aircraft_reg || flight.aircraft_reg || "",

                                // 关键时间信息 - 优先使用快照数据
                                std: flightDisplayInfo.std || (flight.std ? flight.std : ""),
                                sta: flightDisplayInfo.sta || (flight.sta ? flight.sta : ""),
                                etd: flightDisplayInfo.etd || (flight.etd ? flight.etd : ""),
                                eta: flightDisplayInfo.eta || (flight.eta ? flight.eta : ""),

                                status: existingTransaction.status,
                                remarks: existingTransaction.remarks,
                                batchId: existingTransaction.batchId,
                                additionalData: null,
                                createdAt: existingTransaction.createdAt,
                                updatedAt: existingTransaction.updatedAt,

                                // 快照相关信息
                                hasFlightSnapshot: existingTransaction.hasFlightSnapshot || false,
                                flightDataSource: existingTransaction.flightDataSource || "realtime",
                                useSnapshotData: useSnapshot,

                                originalData: {
                                    status: existingTransaction.status,
                                    remarks: existingTransaction.remarks,
                                    supplierId: existingTransaction.supplierId,
                                },
                                details: existingTransaction.details.map((detail) => ({
                                    ...detail,
                                    currentStatus: detail.currentStatus || detail.status || TRANSACTION_STATUS.PENDING,
                                    clientStatus: CLIENT_STATUS.PRISTINE,
                                    originalData: JSON.parse(
                                        JSON.stringify({
                                            ...detail,
                                            currentStatus: detail.currentStatus || detail.status || TRANSACTION_STATUS.PENDING,
                                        }),
                                    ),
                                })),
                            };
                        } else {
                            // 为没有事务的航班创建新的空事务结构
                            // 新事务使用实时航班数据（因为还没有快照）
                            return {
                                id: `new_${flight.id}`,
                                flightId: flight.id,
                                supplierId: selectionManager.selectedSupplier?.id || null,

                                // 航班基本信息
                                flightNumber: flight.flight_number,
                                flightDate: flight.flight_date,
                                route: `${flight.departure_airport}-${flight.arrival_airport}`,
                                departureAirport: flight.departure_airport,
                                arrivalAirport: flight.arrival_airport,
                                aircraftReg: flight.aircraft_reg,

                                // 时间信息
                                std: flight.std,
                                sta: flight.sta,
                                etd: flight.etd,
                                eta: flight.eta,
                                atd: flight.atd,
                                ata: flight.ata,

                                // 事务信息
                                status: null,
                                remarks: null,
                                batchId: null,
                                additionalData: null,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString(),
                                details: [],
                                isNewTransaction: true,

                                // 快照相关信息（新事务暂时没有快照）
                                hasFlightSnapshot: false,
                                flightDataSource: "realtime",
                                useSnapshotData: false,
                            };
                        }
                    });

                    dispatch(actionCreators.setTransactions(allTransactions));

                    // 如果是编辑模式，从现有数据中提取产品信息
                    if (result.workflowMode === "EDIT" && allTransactions.length > 0) {
                        const contractItems = allTransactions.flatMap((t) =>
                            t.details.map((d) => ({
                                id: d.contractItemId,
                                product_name: d.productName,
                                partner_name: d.supplierName,
                                price: d.unitPrice,
                                currency: d.currency,
                                product_unit: d.unit,
                            })),
                        );
                        const uniqueContractItems = contractItems.filter((item, index, self) => index === self.findIndex((i) => i.id === item.id));
                        selectionManager.setSelectedContractItems(uniqueContractItems);
                    }

                    dispatch(actionCreators.setDataLoadingStatus(DATA_LOADING_STATUS.SUCCESS));
                    console.log("✅ TransactionDataProvider: 数据加载完成，工作流模式:", result.workflowMode);

                    // 调试：检查快照数据
                    if (result.transactions) {
                        result.transactions.forEach((tx, index) => {
                            if (index < 3) {
                                // 只检查前3个事务
                                console.log(`🔍 事务 ${tx.id} 快照信息:`, {
                                    hasFlightSnapshot: tx.hasFlightSnapshot,
                                    flightDataSource: tx.flightDataSource,
                                    flightDisplayInfo: tx.flightDisplayInfo,
                                });
                            }
                        });
                    }
                }
            } catch (error) {
                console.error("❌ TransactionDataProvider: 数据加载失败:", error);
                dispatch(actionCreators.setDataLoadingStatus(DATA_LOADING_STATUS.ERROR));
                dispatch(actionCreators.setError("dataLoading", error.message));

                // 加载失败时默认进入创建模式
                dispatch(actionCreators.setWorkflowMode("CREATE"));

                // 创建空的事务结构
                if (state.selectedFlights.length > 0) {
                    // 对于配餐/燃油订单，只匹配始发机场；其他事务类型匹配始发或目的地机场
                    const filteredFlights =
                        selectionManager.activeAirports.length > 0
                            ? state.selectedFlights.filter((flight) => (transactionType === "CATERING_ORDER" || transactionType === "FUEL_REQUEST" ? selectionManager.activeAirports.includes(flight.departure_airport) : selectionManager.activeAirports.includes(flight.departure_airport) || selectionManager.activeAirports.includes(flight.arrival_airport)))
                            : state.selectedFlights;

                    const emptyTransactions = filteredFlights.map((flight) => ({
                        id: `new_${flight.id}`,
                        flightId: flight.id,
                        supplierId: selectionManager.selectedSupplier?.id || null,

                        // 航班基本信息
                        flightNumber: flight.flight_number,
                        flightDate: flight.flight_date,
                        route: `${flight.departure_airport}-${flight.arrival_airport}`,
                        departureAirport: flight.departure_airport,
                        arrivalAirport: flight.arrival_airport,
                        aircraftReg: flight.aircraft_reg,

                        // 时间信息
                        std: flight.std,
                        sta: flight.sta,
                        etd: flight.etd,
                        eta: flight.eta,
                        atd: flight.atd,
                        ata: flight.ata,

                        // 事务信息
                        status: null,
                        remarks: null,
                        batchId: null,
                        additionalData: null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        details: [],
                        isNewTransaction: true,

                        // 快照相关信息（错误恢复时的新事务暂时没有快照）
                        hasFlightSnapshot: false,
                        flightDataSource: "realtime",
                        useSnapshotData: false,
                    }));

                    dispatch(actionCreators.setTransactions(emptyTransactions));
                }
            }
        };

        loadTransactionData();
    }, [state.selectedFlights, state.transactionType, selectionManager.selectedSupplier, selectionManager.activeAirports, state.dataLoadingStatus, lastInitializedKey, initializeBatchTransactionData, orchestrator, selectionManager]);

    // 监听机场变更，当机场变更时清空事务数据
    useEffect(() => {
        // 当机场变更时，如果当前有事务数据，清空事务数据
        if (selectionManager.activeAirports.length > 0 && state.transactions.length > 0) {
            // 检查当前事务是否都匹配新的机场选择
            const shouldClearTransactions = state.transactions.some((transaction) => {
                const flight = state.selectedFlights.find((f) => f.id === transaction.flightId);
                if (!flight) return true; // 如果找不到对应的航班，应该清除

                // 检查航班是否匹配当前选择的机场
                // 对于配餐/燃油订单，只匹配始发机场；其他事务类型匹配始发或目的地机场
                if (transactionType === "CATERING_ORDER" || transactionType === "FUEL_REQUEST") {
                    return !selectionManager.activeAirports.includes(flight.departure_airport);
                } else {
                    return !(selectionManager.activeAirports.includes(flight.departure_airport) || selectionManager.activeAirports.includes(flight.arrival_airport));
                }
            });

            if (shouldClearTransactions) {
                console.log("🔄 机场变更，清空不匹配的事务数据");
                dispatch(actionCreators.setTransactions([]));
                dispatch(actionCreators.setDataLoadingStatus(DATA_LOADING_STATUS.IDLE));
            }
        }
    }, [selectionManager.activeAirports, state.transactions, state.selectedFlights, dispatch, transactionType]);

    const contextValue = {
        // 状态
        state,

        // 基础操作
        setLoading,
        setDataLoadingStatus,
        setWorkflowMode,
        setTransactions,
        resetState,

        // 错误处理
        setError,
        clearError,

        // UI状态
        setUIState,

        // 事务管理器
        transactionManager,

        // 编排器
        orchestrator,
    };

    return <TransactionDataContext.Provider value={contextValue}>{children}</TransactionDataContext.Provider>;
};

/**
 * Hook for using transaction data context
 */
export const useTransactionDataContext = () => {
    const context = useContext(TransactionDataContext);
    if (!context) {
        throw new Error("useTransactionDataContext must be used within a TransactionDataProvider");
    }
    return context;
};

export default TransactionDataContext;
